import {
  ExtractDocumentTypeFromTypedRxJsonSchema,
  RxJsonSchema,
  toTypedRxJsonSchema,
} from 'rxdb'

import {
  AssessmentSupportIsEnsured,
  CareCounselingIndicatedSection7A,
  CareCounselingRequestedSection7A,
  ConsentToProcessingByInsuranceSection45,
  ConsentToProcessingCounselingResultsSection7A,
  ConsentToShareCareSupportInformation,
  ConsentToShareImprovementRecommendations,
  ConsentToSiteInspectionAndToShareInformation,
  ConsultationParticipant,
  CounselingViaVideoConference,
  ProtocolDispatch,
  SupportSituationImproveMeasures,
  SupportSituationImproveMeasuresSuggestions,
  TrainingSubjects,
} from '@/gql/graphql'
import { addNullToEnum } from '@/system/rxdb/utils.ts'

export const assessmentAdditionalInfo = {
  version: 0,
  title: 'AssessmentAdditionalInfo',
  primaryKey: 'rowId',
  type: 'object',
  properties: {
    rowId: {
      maxLength: 36,
      type: 'string',
    },
    updatedAt: {
      type: 'string',
    },
    assessmentId: {
      ref: 'assessment',
      type: 'string',
    },
    careChangesSuggestionsPersonInNeedOfCare: {
      type: ['string', 'null'],
    },
    careChangesSuggestionsRelativeCaregiver: {
      type: ['string', 'null'],
    },
    careImprovementsSuggestionsPersonInNeedOfCare: {
      type: ['string', 'null'],
    },
    careImprovementsSuggestionsRelativeCaregiver: {
      type: ['string', 'null'],
    },
    differentThanUsualPersonInNeedOfCare: {
      type: ['string', 'null'],
    },
    differentThanUsualRelativeCaregiver: {
      type: ['string', 'null'],
    },
    otherCommentsPersonInNeedOfCare: {
      type: ['string', 'null'],
    },
    otherCommentsRelativeCaregiver: {
      type: ['string', 'null'],
    },
    agreedObjectives: {
      type: ['string', 'null'],
    },
    agreementsOnNextSteps: {
      type: ['string', 'null'],
    },
    openQuestions: {
      type: ['string', 'null'],
    },
    supportIsEnsured: {
      type: ['string', 'null'],
      enum: addNullToEnum(AssessmentSupportIsEnsured),
    },
    supportSituationImproveMeasures: {
      type: ['string', 'null'],
      enum: addNullToEnum(SupportSituationImproveMeasures),
    },
    supportSituationImproveMeasuresSuggestions: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: addNullToEnum(SupportSituationImproveMeasuresSuggestions),
      },
    },
    supportSituationImproveMeasuresSuggestionsOtherComments: {
      type: ['string', 'null'],
    },
    counselingViaVideoConference: {
      type: ['string', 'null'],
      enum: addNullToEnum(CounselingViaVideoConference),
    },
    careCounselingIndicatedSection7A: {
      type: ['string', 'null'],
      enum: addNullToEnum(CareCounselingIndicatedSection7A),
    },
    careCounselingRequestedSection7A: {
      type: ['string', 'null'],
      enum: addNullToEnum(CareCounselingRequestedSection7A),
    },
    consentToShareCareSupportInformation: {
      type: ['string', 'null'],
      enum: addNullToEnum(ConsentToShareCareSupportInformation),
    },
    consentToShareImprovementRecommendations: {
      type: ['string', 'null'],
      enum: addNullToEnum(ConsentToShareImprovementRecommendations),
    },
    consentToSiteInspectionAndToShareInformation: {
      type: ['string', 'null'],
      enum: addNullToEnum(ConsentToSiteInspectionAndToShareInformation),
    },
    consentToProcessingCounselingResultsSection7A: {
      type: ['string', 'null'],
      enum: addNullToEnum(ConsentToProcessingCounselingResultsSection7A),
    },
    consentToProcessingByInsuranceSection45: {
      type: ['string', 'null'],
      enum: addNullToEnum(ConsentToProcessingByInsuranceSection45),
    },
    insuranceName: {
      type: ['string', 'null'],
    },
    locationAndDateSection373: {
      type: ['string', 'null'],
    },
    supportIsEnsuredReason: {
      type: ['string', 'null'],
    },
    supportSituationCaregiverPerspective: {
      type: ['string', 'null'],
    },
    supportSituationCounselorPerspective: {
      type: ['string', 'null'],
    },
    protocolDispatch: {
      type: ['string', 'null'],
      enum: addNullToEnum(ProtocolDispatch),
    },
    protocolDispatchEmail: {
      type: ['string', 'null'],
    },
    trainingSubject: {
      type: ['string', 'null'],
      enum: addNullToEnum(TrainingSubjects),
    },
    billablePerson: {
      type: ['string', 'null'],
      enum: addNullToEnum(ConsultationParticipant),
    },
  },
  required: ['rowId', 'updatedAt', 'assessmentId'],
} as const

const schemaTyped = toTypedRxJsonSchema(assessmentAdditionalInfo)

export type AssessmentAdditionalInfo = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>

export const assessmentAdditionalInfoSchema: RxJsonSchema<AssessmentAdditionalInfo> =
  assessmentAdditionalInfo
